import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useRef, useState } from 'react';

// Actions
import { showErrorMessage } from '@actions/messageconfirmation';

// Services
import productionPlanningService from '@services/productionPlanning';

// Common
import ExportModalContent from '@lib/inpulse/ExportModal';

import { downloadFileFromBase64 } from '@admin/products/recipes/utils/downloadRecipe';

// The PDF is generated in a single request, so the progress bar is simulated while waiting for the response
const AUTO_PROGRESS_INTERVAL_MS = 400;
const AUTO_PROGRESS_INCREMENT = 8;
const MAX_AUTO_PROGRESS = 90;

/**
 * Displays a modal with a progress bar while the detailed production planning PDF is generated,
 * then triggers its download once ready.
 *
 * @param {{
 *  params: {
 *    storeId: string
 *    payload: object
 *  }
 *  closeModal: () => void
 * }} props
 *
 * @return {JSX.Element}
 */
export const PdfGenerationModal = (props) => {
  const {
    params: { storeId, payload },
    closeModal,
    showErrorMessage,
  } = props;

  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Ref instead of state so the async generation callback reads the up-to-date value
  const isCancelledRef = useRef(false);

  const exitModal = () => {
    isCancelledRef.current = true;

    setIsLoading(false);
    closeModal();
  };

  useEffect(() => {
    const autoProgressInterval = setInterval(() => {
      setProgress((currentProgress) =>
        Math.min(currentProgress + AUTO_PROGRESS_INCREMENT, MAX_AUTO_PROGRESS),
      );
    }, AUTO_PROGRESS_INTERVAL_MS);

    (async () => {
      try {
        const { fileName, rawBase64File } =
          await productionPlanningService.downloadDetailedProductionPlanningPDF(storeId, payload);

        clearInterval(autoProgressInterval);

        if (isCancelledRef.current) {
          return;
        }

        setProgress(100);

        downloadFileFromBase64(rawBase64File, fileName);

        setIsLoading(false);
        closeModal();
      } catch {
        clearInterval(autoProgressInterval);

        if (isCancelledRef.current) {
          return;
        }

        showErrorMessage(i18next.t('FORECAST.SALES_PLANNING.PDF_GENERATION_ERROR'));

        setIsLoading(false);
        closeModal();
      }
    })();

    return () => clearInterval(autoProgressInterval);
  }, []);

  return (
    <ExportModalContent
      {...props}
      closeModal={closeModal}
      exitModal={exitModal}
      isLoading={isLoading}
      progress={progress}
      setLoading={setIsLoading}
      titleModal={i18next.t('FORECAST.SALES_PLANNING.PDF_GENERATION_MODAL_TITLE')}
    />
  );
};

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(null, mapDispatchToProps)(PdfGenerationModal);
